<template>
  <div class="row">
    <!-- ABOUT -->
    <div class="col-12 col-lg-8 mb-4 mb-lg-0">
      <h2 class="be-intro__title mb-3">
        {{ $t('general.about-accommodation') }}
      </h2>
      <div
        v-if="hasDescription"
        class="be-intro__desc html-desc"
        :class="{ 'be-intro__desc--clamped': !isExpanded }"
        v-html="getMerchant.description"></div>
      <p v-else class="be-intro__desc text-muted mb-0">{{ getMerchant.name }}</p>
      <button
        v-if="hasDescription"
        class="be-intro__toggle mt-2"
        @click="isExpanded = !isExpanded">
        {{ isExpanded ? $t('hotel.show-less') : $t('hotel.show-more') }}
      </button>

      <!-- FACILITIES -->
      <template v-if="hasFacilities">
        <hr class="be-intro__divider" />
        <h2 class="be-intro__title mb-3">
          {{ $t('general.facilities') }}
        </h2>
        <div class="be-intro__facilities">
          <div
            v-for="facility in visibleFacilities"
            :key="facility.id"
            class="be-intro__facility">
            <span
              v-if="facility.svg_tag"
              class="be-intro__facility-icon"
              v-html="facility.svg_tag"></span>
            <img
              v-else
              :src="facility.image_url"
              :alt="facility.name"
              class="be-intro__facility-icon" />
            <p class="be-intro__facility-name mb-0">{{ facility.name }}</p>
          </div>
          <button
            v-if="hasHiddenFacilities"
            class="be-intro__facility be-intro__facility--toggle"
            @click="isFacilitiesModalOpen = true">
            <span class="be-intro__facility-count">+{{ hiddenFacilityCount }}</span>
            <span class="be-intro__facility-name">
              {{ $t('general.see-all-facilities') }}
              <ChevronRight :size="18" class="be-intro__facility-chevron" />
            </span>
          </button>
        </div>
        <BeFacilitiesModal
          :is-open="isFacilitiesModalOpen"
          :categories="facilityCategories"
          @close="isFacilitiesModalOpen = false" />
      </template>
    </div>

    <!-- BOOK CARD -->
    <div class="col-12 col-lg-4">
      <div class="be-intro__card">
        <h3 class="be-intro__card-title mb-3">{{ bookTitle }}</h3>
        <template v-if="startingPrice > 0">
          <p class="be-intro__card-label mb-1">{{ $t('hotel.starting-from') }}</p>
          <p class="be-intro__card-price mb-3">
            {{ $formatCurrency(startingPrice) }}
            <span class="be-intro__card-unit">/ {{ $t('hotel.room') }}</span>
          </p>
        </template>
        <button class="be-intro__card-cta button-primary w-100 be-press" @click="$emit('check-availability')">
          {{ $t('hotel.find-rooms') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';
import { ChevronRight } from 'lucide-vue';
import {
  fetchMerchantFacilityCategories,
  flattenFacilityCategories,
} from '@/utils/merchantFacility.js';
import { getBookOurTitleKey } from '@/utils/merchantCategory.js';
import BeFacilitiesModal from '@/components/hotel/be/v3/BeFacilitiesModal.vue';

const VISIBLE_FACILITY_LIMIT = 7;

export default {
  name: 'BeMerchantIntro',
  components: {
    ChevronRight,
    BeFacilitiesModal,
  },
  props: {
    startingPrice: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      isExpanded: false,
      isFacilitiesModalOpen: false,
      facilityCategories: [],
    };
  },
  computed: {
    ...mapGetters(['getMerchant']),
    hasDescription() {
      const description = this.getMerchant.description;
      return this.$isNotEmptyV2(description);
    },
    facilities() {
      return flattenFacilityCategories(this.facilityCategories);
    },
    hasFacilities() {
      return this.facilities.length > 0;
    },
    visibleFacilities() {
      if (!this.hasHiddenFacilities) return this.facilities;
      return this.facilities.slice(0, VISIBLE_FACILITY_LIMIT);
    },
    hiddenFacilityCount() {
      return this.facilities.length - VISIBLE_FACILITY_LIMIT;
    },
    hasHiddenFacilities() {
      return this.hiddenFacilityCount > 0;
    },
    bookTitle() {
      return this.$t(getBookOurTitleKey(this.getMerchant.merchant_category_name));
    },
  },
  async mounted() {
    this.facilityCategories = await fetchMerchantFacilityCategories(this.getMerchant);
  },
};
</script>

<style scoped>
.be-intro__title {
  font-family: var(--be-v3-title-font, BeV3Sans), sans-serif;
  font-weight: 600;
  color: #1a1a2e;
  font-size: 22px;
}
.be-intro__desc {
  color: #555;
  font-size: 15px;
  line-height: 1.7;
  /* -webkit-line-clamp cannot be transitioned, so the two states also carry a
     max-height the browser can interpolate. The expanded cap is deliberately
     generous — it only has to exceed any real description. */
  max-height: 2000px;
  overflow: hidden;
  transition: max-height var(--be-v3-dur-slow, 300ms) var(--be-v3-ease-inout, ease-in-out);
}
.be-intro__desc--clamped {
  display: -webkit-box;
  -webkit-line-clamp: 5;
  -webkit-box-orient: vertical;
  max-height: calc(15px * 1.7 * 5);
}
.be-intro__toggle {
  background: none;
  border: none;
  padding: 0;
  color: var(--merchant-primary-color, #79a33a);
  font-weight: 600;
  cursor: pointer;
  transition: opacity var(--be-v3-dur-fast, 150ms) var(--be-v3-ease, ease-out);
}
.be-intro__toggle:hover {
  opacity: 0.75;
}
.be-intro__divider {
  margin: 28px 0;
  border-top: 1px solid #e8e8e8;
}
.be-intro__facilities {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}
@media (max-width: 991.98px) {
  .be-intro__facilities {
    grid-template-columns: repeat(2, 1fr);
  }
  /* full-width action across the pair of columns rather than a card-sized tile */
  .be-intro__facility--toggle {
    grid-column: 1 / -1;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    min-height: 0;
    padding: 16px 20px;
  }
  .be-intro__facility--toggle .be-intro__facility-count,
  .be-intro__facility--toggle .be-intro__facility-chevron {
    display: none;
  }
}
.be-intro__facility {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  gap: 16px;
  min-height: 80px;
  padding: 20px;
  background: #fff;
  border: 1px solid #e8e8e8;
  border-radius: var(--be-v3-radius, 14px);
  text-align: left;
}
.be-intro__facility--toggle {
  cursor: pointer;
}
.be-intro__facility-icon {
  width: 28px;
  height: 28px;
  object-fit: contain;
}
.be-intro__facility-icon >>> svg {
  width: 100%;
  height: 100%;
}
.be-intro__facility-name {
  color: #1a1a2e;
  font-size: 16px;
  font-weight: 400;
  display: flex;
  align-items: center;
  gap: 6px;
}
.be-intro__facility-count {
  font-weight: 700;
  font-size: 22px;
  color: #1a1a2e;
  line-height: 1;
}
.be-intro__facility-chevron {
  flex-shrink: 0;
}
.be-intro__card {
  border: 1px solid #e8e8e8;
  border-radius: var(--be-v3-radius, 14px);
  padding: 24px;
  box-shadow: 0 6px 24px rgba(0, 0, 0, 0.06);
  position: sticky;
  top: 88px;
}
.be-intro__card-title {
  font-family: var(--be-v3-title-font, BeV3Sans), sans-serif;
  font-weight: 600;
  color: #1a1a2e;
  font-size: 20px;
}
.be-intro__card-label {
  color: #777;
  font-size: 14px;
}
.be-intro__card-price {
  font-weight: 900;
  color: #1a1a2e;
  font-size: 24px;
}
.be-intro__card-unit {
  font-size: 14px;
  font-weight: 400;
  color: #777;
}
.be-intro__card-cta {
  border-radius: var(--be-v3-button-radius, 8px);
  padding: 12px;
}
</style>



// WEBPACK FOOTER //
// src/components/hotel/be/v3/BeMerchantIntro.vue